import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
// import {
//   AppBar,
//   IconButton,
//   Menu,
//   MenuItem,
//   Toolbar,
//   Typography,
// } from '@mui/material';
// import {
//   Menu as MenuIcon,
//   AccountCircle,
//   Business as BusinessIcon,
// } from '@mui/icons-material';
import { useUser } from '../OidcProvider/OidcProvider';
import GlobalNavigator from '../GlobalNav';
import { DummyNavBar } from './DummyNavBar';
import { LocalDevSideNav } from './LocalDevSideNav';
import LoadingSpinner from '../LoadingSpinner';
import AppConfig from '../../AppConfig';
import { OnboardingWizardProvider } from '../OnboardingWizard/OnboardingWizardProvider';
import { useContentAuthoringEditorNavCollapse } from '../../hooks/useContentAuthoringEditorNavCollapse';
import { CourseTranslationOutcomeToastHost } from '../ContentAuthoring/CourseTranslationOutcomeToastHost';

export function Layout() {
  const { userProfile } = useUser();
  const [isNavLoaded, setIsNavLoaded] = React.useState(false);
  const [isSideNavOpen, setIsSideNavOpen] = React.useState(true);
  const navLoadTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Reset nav loaded state when user logs in/out
  React.useEffect(() => {
    if (userProfile) {
      // User just logged in
      if (AppConfig.isLocal) {
        // DummyNavBar is instant, no loading needed
        setIsNavLoaded(true);
      } else {
        // GlobalNavigator needs to load
        setIsNavLoaded(false);
        
        // Fallback: If GlobalNavigator doesn't load within 5 seconds, show content anyway
        navLoadTimeoutRef.current = setTimeout(() => {
          console.warn('[Layout] GlobalNavigator load timeout, showing content anyway');
          setIsNavLoaded(true);
        }, 5000);
        
        // Cleanup: clear timeout if component unmounts or userProfile changes
        return () => {
          if (navLoadTimeoutRef.current) {
            clearTimeout(navLoadTimeoutRef.current);
            navLoadTimeoutRef.current = null;
          }
        };
      }
    } else {
      // User logged out or not logged in
      setIsNavLoaded(true);
    }
  }, [userProfile]);

  const handleNavLoaded = () => {
    // Clear the timeout since nav loaded successfully
    if (navLoadTimeoutRef.current) {
      clearTimeout(navLoadTimeoutRef.current);
      navLoadTimeoutRef.current = null;
    }
    setIsNavLoaded(true);
  };

  useContentAuthoringEditorNavCollapse({
    isLocalSideNavOpen: isSideNavOpen,
    setIsLocalSideNavOpen: setIsSideNavOpen,
    isDeployedNavReady: isNavLoaded,
  });

  // Old code - moved to DummyNavBar component
  // const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  //
  // const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };
  //
  // const handleProfileMenuClose = () => {
  //   setAnchorEl(null);
  // };
  //
  // const handleLogout = async () => {
  //   handleProfileMenuClose();
  //   try {
  //     window.sessionStorage.removeItem('accessToken');
  //     await userManager.signoutRedirect();
  //   } catch (error) {
  //     console.error('Error during logout:', error);
  //     window.sessionStorage.removeItem('accessToken');
  //   }
  // };

  return (
    /*
      `height`, not `minHeight`. A page that wants to fill the viewport and scroll its own panes
      independently — the template editor's slide rail is the case — needs a definite height to
      resolve `height: 100%` against. With `minHeight` the chain is indefinite all the way down,
      every descendant grows to its content, and the pane-level `overflow-y: auto` never engages:
      the whole page scrolls as one instead. Ordinary tall pages are unaffected, because `main`
      below still scrolls and its children still size to their content.
    */
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100svh' }}>
      {userProfile ? (
        <>
          <CourseTranslationOutcomeToastHost />
          {/* Global Navigator for deployed environments */}
          {!AppConfig.isLocal && <GlobalNavigator onLoad={handleNavLoaded} />}

          {/* Dummy Navigation Bar for Local Development */}
          {AppConfig.isLocal && (
            <DummyNavBar
              isSideNavOpen={isSideNavOpen}
              onSideNavToggle={() => setIsSideNavOpen((open) => !open)}
            />
          )}

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <OnboardingWizardProvider>
            {/* Show full-screen loading spinner while nav is loading in deployed env */}
            {!isNavLoaded && !AppConfig.isLocal ? (
              <LoadingSpinner />
            ) : AppConfig.isLocal ? (
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  minHeight: 0,
                  maxHeight: 'calc(100svh - var(--atlas-global-header-height))',
                }}
              >
                <LocalDevSideNav open={isSideNavOpen} />
                <Box
                  component="main"
                  data-atlas-gn-app="true"
                  sx={{
                    flexGrow: 1,
                    px: 2,
                    p: 3,
                    overflowY: 'auto',
                  }}
                >
                  <Outlet />
                </Box>
              </Box>
            ) : (
              <Box
                component="main"
                data-atlas-gn-app="true"
                sx={{
                  flexGrow: 1,
                  px: 2,
                  p: 3,
                  maxHeight: 'calc(100svh - var(--atlas-global-header-height))',
                  overflowY: 'auto',
                }}
              >
                <Outlet />
              </Box>
            )}
            </OnboardingWizardProvider>
          </Box>
        </>
      ) : (
        /* Welcome Page when not logged in */
        <Box sx={{ px: 2 }}>
          <Outlet />
        </Box>
      )}
    </Box>
  );
}
